import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createTaxonomyService } from "@/core/services/factory";
import type { PromotedCategory } from "@/core/services/types/promoted-category.types";
import { useTranslations } from "@/lib/useTranslations";

interface PromotedCategoriesPanelProps {
  items: PromotedCategory[];
}

function typeLabelKey(type: PromotedCategory["type"]) {
  switch (type) {
    case "artForms":
      return "typeArtForms" as const;
    case "artisticPeriods":
      return "typeArtisticPeriods" as const;
    case "artisticPeriodsSubCats":
      return "typeArtisticPeriodSubCats" as const;
  }
}

export function PromotedCategoriesPanel({ items }: PromotedCategoriesPanelProps) {
  const t = useTranslations("taxonomy");
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (next: PromotedCategory[]) =>
      createTaxonomyService().replacePromotedCategories(next),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.setQueryData(["taxonomy-promoted"], result.data);
      }
      void queryClient.invalidateQueries({ queryKey: ["taxonomy-promoted"] });
    },
  });

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    const row = next[index];
    if (!row) return;
    next.splice(index, 1);
    next.splice(target, 0, row);
    mutation.mutate(next);
  };

  const removeAt = (index: number) => {
    const next = items.filter((_, i) => i !== index);
    mutation.mutate(next);
  };

  return (
    <section className="wireframe-border space-y-3 bg-whart-bg p-4">
      <div className="space-y-1">
        <h2 className="text-sm font-semibold text-whart-text">
          {t("promoted.title")}
        </h2>
        <p className="text-sm text-neutral-600">{t("promoted.subtitle")}</p>
      </div>

      {mutation.isError || (mutation.data && !mutation.data.success) ? (
        <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {t("promoted.saveError")}
        </p>
      ) : null}

      {items.length === 0 ? (
        <p className="px-1 py-4 text-sm text-neutral-500">
          {t("promoted.empty")}
        </p>
      ) : (
        <ul className="divide-y divide-neutral-100 rounded-xl border border-black/10">
          {items.map((item, index) => (
            <li
              key={`${item.type}:${item.category}`}
              className="flex items-center gap-3 px-3 py-2.5"
            >
              <img
                src={item.img}
                alt=""
                width={16}
                height={16}
                className="size-4 shrink-0"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-whart-text">
                  {item.category}
                </p>
                <p className="text-xs text-neutral-500">
                  {t(`promoted.${typeLabelKey(item.type)}`)}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={mutation.isPending || index === 0}
                  onClick={() => move(index, -1)}
                  className="rounded-xl border border-black/10 px-2 py-1 text-xs text-neutral-600 disabled:opacity-40"
                  aria-label={t("promoted.moveUp")}
                >
                  ↑
                </button>
                <button
                  type="button"
                  disabled={mutation.isPending || index === items.length - 1}
                  onClick={() => move(index, 1)}
                  className="rounded-xl border border-black/10 px-2 py-1 text-xs text-neutral-600 disabled:opacity-40"
                  aria-label={t("promoted.moveDown")}
                >
                  ↓
                </button>
                <button
                  type="button"
                  disabled={mutation.isPending}
                  onClick={() => removeAt(index)}
                  className="rounded-xl border border-black/10 px-2 py-1 text-xs text-neutral-600 hover:bg-zinc-50 disabled:opacity-40"
                >
                  {t("promoted.remove")}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
